/**
 * 通用配置状态管理器
 * 统一管理所有通用配置值，确保数据同步
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import commonConfigManager from '../utils/commonConfigManager';

interface ContactInfo {
  officePhone: string;
  preAuditPhone: string;
  contactEmail: string;
  contactAddress: string;
  postalCode: string;
}

interface WebsiteInfo {
  websiteTitle: string;
  websiteSubtitle: string;
  hostUnit: string;
  copyright: string;
}

interface RecordInfo {
  websiteId: string;
  icpRecord: string;
  policeRecord: string;
}

export interface CommonConfigState {
  // 联系我们配置
  contactInfo: ContactInfo;
  // 网站信息配置
  websiteInfo: WebsiteInfo;
  // 备案信息配置
  recordInfo: RecordInfo;
  // 是否已初始化
  initialized: boolean;
  // 刷新配置
  refresh: () => Promise<void>;
}

// 从配置数据中提取联系我们的配置
const extractContactInfo = (configs: any[]): ContactInfo => {
  const result: ContactInfo = {
    officePhone: '',
    preAuditPhone: '',
    contactEmail: '',
    contactAddress: '',
    postalCode: ''
  };
  
  configs.forEach((config: any) => {
    if (config.status === '1') {
      switch (config.configKey) {
        case 'office_phone':
          result.officePhone = config.configValue || '';
          break;
        case 'pre_audit_phone':
          result.preAuditPhone = config.configValue || '';
          break;
        case 'contact_email':
          result.contactEmail = config.configValue || '';
          break;
        case 'contact_address':
          result.contactAddress = config.configValue || '';
          break;
        case 'postal_code':
          result.postalCode = config.configValue || '';
          break;
      }
    }
  });
  
  return result;
};

// 从配置数据中提取网站信息的配置
const extractWebsiteInfo = (configs: any[]): WebsiteInfo => {
  const result: WebsiteInfo = {
    websiteTitle: '',
    websiteSubtitle: '',
    hostUnit: '',
    copyright: ''
  };
  
  configs.forEach((config: any) => {
    if (config.status === '1') {
      switch (config.configKey) {
        case 'website_title':
          result.websiteTitle = config.configValue || '';
          break;
        case 'website_subtitle':
          result.websiteSubtitle = config.configValue || '';
          break;
        case 'host_unit':
          result.hostUnit = config.configValue || '';
          break;
        case 'copyright':
          result.copyright = config.configValue || '';
          break;
      }
    }
  });
  
  return result;
};

// 从配置数据中提取备案信息的配置
const extractRecordInfo = (configs: any[]): RecordInfo => {
  const result: RecordInfo = {
    websiteId: '',
    icpRecord: '',
    policeRecord: ''
  };
  
  configs.forEach((config: any) => {
    if (config.status === '1') {
      switch (config.configKey) {
        case 'website_id':
          result.websiteId = config.configValue || '';
          break;
        case 'icp_record':
          result.icpRecord = config.configValue || '';
          break;
        case 'police_record':
          result.policeRecord = config.configValue || '';
          break;
      }
    }
  });
  
  return result;
};

// 创建 Context
export const CommonConfigContext = createContext<CommonConfigState | null>(null);

// Hook 用于获取配置状态
export const useCommonConfigStore = (): CommonConfigState => {
  const context = useContext(CommonConfigContext);
  if (!context) {
    throw new Error('useCommonConfigStore must be used within CommonConfigProvider');
  }
  return context;
};

// 配置管理逻辑
export const useCommonConfigLogic = () => {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    officePhone: '',
    preAuditPhone: '',
    contactEmail: '',
    contactAddress: '',
    postalCode: ''
  });
  
  const [websiteInfo, setWebsiteInfo] = useState<WebsiteInfo>({
    websiteTitle: '',
    websiteSubtitle: '',
    hostUnit: '',
    copyright: ''
  });
  
  const [recordInfo, setRecordInfo] = useState<RecordInfo>({
    websiteId: '',
    icpRecord: '',
    policeRecord: ''
  });
  
  const [initialized, setInitialized] = useState(false);

  // 加载配置数据（优先从缓存读取，只在缓存不存在时才从API获取）
  const loadConfigs = useCallback(async () => {
    try {
      // 初始化管理器（只从localStorage加载缓存，不调用API）
      await commonConfigManager.initialize();
      
      // 尝试从缓存获取配置数据，如果缓存不存在才调用API
      // preloadAllConfigs 会检查缓存，只在缓存不存在时才调用API
      await commonConfigManager.preloadAllConfigs();
      
      // 获取所有配置数据（优先从缓存读取）
      const contactConfigs = await commonConfigManager.getConfigData('contact_info');
      const websiteConfigs = await commonConfigManager.getConfigData('website_info');
      const recordConfigs = await commonConfigManager.getConfigData('record_info');
      
      // 提取并更新状态
      setContactInfo(extractContactInfo(contactConfigs));
      setWebsiteInfo(extractWebsiteInfo(websiteConfigs));
      setRecordInfo(extractRecordInfo(recordConfigs));
      setInitialized(true);
    } catch (error) {
      console.error('初始化通用配置状态管理器失败:', error);
    }
  }, []);

  // 刷新配置
  const refresh = useCallback(async () => {
    try {
      // 清除缓存
      commonConfigManager.clearAllCache();
      
      // 重新加载所有配置数据
      await commonConfigManager.preloadAllConfigs();
      
      // 获取所有配置数据
      const contactConfigs = await commonConfigManager.getConfigData('contact_info');
      const websiteConfigs = await commonConfigManager.getConfigData('website_info');
      const recordConfigs = await commonConfigManager.getConfigData('record_info');
      
      // 提取并更新状态
      setContactInfo(extractContactInfo(contactConfigs));
      setWebsiteInfo(extractWebsiteInfo(websiteConfigs));
      setRecordInfo(extractRecordInfo(recordConfigs));
    } catch (error) {
      console.error('刷新通用配置状态失败:', error);
    }
  }, []);

  // 组件挂载时初始化
  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  return {
    contactInfo,
    websiteInfo,
    recordInfo,
    initialized,
    refresh
  };
};